/**
 * Фишка игрока разметкой: аватарка и маркер в лобби и в диалогах, где сцены движка нет. Те же числа, что у фишки на
 * сцене (`pawn.ts`), но рисует их React и SVG.
 *
 * FUTURE: уйдёт, когда фишка придёт из Figma целиком (dotLottie) и её покажет движок.
 */

import { useId, useLayoutEffect, useState, useRef, type CSSProperties } from 'react'
import { PAWNS } from '../../srv/protocol.js'
import { avatarSheetUrl, useSheets } from '../assets.js'
import { PLAYER_AVATARS } from './avatars.js'
import { PAWN_FRAME, PAWN_NAME, PAWN_PLATE, PAWN_SHADOW, PAWN_WELL } from './pawn.js'

/**
 * Листы аватарок в документе. Ставятся один раз на страницу: `use` внутри
 * фишки ищет рисунок по идентификатору, а не по дереву, поэтому лежать они
 * могут где угодно — лишь бы лежали. Бокс нулевой и ничего не перекрывает.
 */
export function AvatarSprite() {
  const sheets = useSheets([avatarSheetUrl])
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute h-0 w-0 overflow-hidden"
      dangerouslySetInnerHTML={{ __html: sheets }}
    />
  )
}

/** Капля: место в кадре и её собственный бокс. */
const PIN = { x: 150, y: 400, w: 900, h: PAWN_FRAME.tip - 400 }
const PIN_BODY =
  'M 450 1340 C 450 1340 0 760 0 450 C 0 201.47 201.47 0 450 0 ' +
  'C 698.53 0 900 201.47 900 450 C 900 760 450 1340 450 1340 Z'
/*
 * Аватарка вписана в окно ровно так же, как в выборе аватарки: рисунок занимает
 * круг целиком и не выходит за него. Числа выведены из самого окна, чтобы рисунок ехал за ним.
 */
const AVATAR = { x: PAWN_WELL.cx - PAWN_WELL.r, y: PAWN_WELL.cy - PAWN_WELL.r, size: PAWN_WELL.r * 2 }
/** Доля кегля до верха прописной: по ней строка стоит в центре плашки. */
const NAME_CAP = 0.72

type PlayerAvatar = (typeof PLAYER_AVATARS)[number]

/** Цвета маркера — имена фишек; значения лежат один раз, в палитре `index.main.css`. */
const PLAYER_COLORS = PAWNS.map((p) => p.id)

/** Значение цвета маркера по имени из палитры. */
export const markerColor = (name: string): string => `var(--color-marker-${name})`

type PlayerColor = (typeof PLAYER_COLORS)[number]

interface PlayerProps {
  avatar?: PlayerAvatar
  /** Имя цвета из палитры. */
  color?: PlayerColor
  /** Ширина фишки в пикселях. */
  size?: number
  /** Точка вставки в пикселях. */
  x?: number
  y?: number
  /** Имя игрока под аватаркой. Пусто — нет ни подписи, ни затемнения. */
  name?: string
}

const DEFAULT_AVATAR: PlayerAvatar = 'girl-2'
const DEFAULT_COLOR: PlayerColor = 'blue'

/**
 * Контур свободного места: пунктир по силуэту капли маркера, в том же кадре, что
 * и сам маркер, — занятое место и свободное совпадают по форме и положению.
 * Штрих не масштабируется вместе с кадром (`non-scaling-stroke`): толщина 1 и
 * штрих 3 через 3 экранных пикселя — тот же рисунок, что у пунктирной рамки
 * (`border dashed` в 1px) в остальных местах интерфейса, на любой фишке.
 * Контур — свободное место, в центре окна аватарки у него знак вопроса.
 */
export function PinOutline({ size }: { size: number }) {
  return (
    <svg
      viewBox={`${PAWN_FRAME.x} ${PAWN_FRAME.y} ${PAWN_FRAME.w} ${PAWN_FRAME.h}`}
      width={size}
      height={(size * PAWN_FRAME.h) / PAWN_FRAME.w}
      aria-hidden="true"
      className="block"
    >
      <g transform={`translate(${PIN.x} ${PIN.y})`}>
        <path
          d={PIN_BODY}
          fill="none"
          stroke="currentColor"
          strokeWidth={1}
          strokeDasharray="3 3"
          vectorEffect="non-scaling-stroke"
        />
      </g>
      {/* Свободное место: вопрос по центру окна аватарки — «кто здесь будет?». */}
      <text
        x={PAWN_WELL.cx}
        y={PAWN_WELL.cy}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={PAWN_WELL.r * 1.6}
        fontWeight={200}
        fill="currentColor"
        fillOpacity={0.7}
        aria-hidden="true"
      >
        ?
      </text>
    </svg>
  )
}

export function Player({
  avatar = DEFAULT_AVATAR,
  color = DEFAULT_COLOR,
  name,
  size = 120,
  x,
  y,
}: PlayerProps) {
  const uid = useId().replace(/:/g, '')
  const g = (name: string) => `player-${name}-${uid}`

  /*
   * Плашка обнимает имя, а ширину имени знает только раскладка: гарнитура тут
   * системная и от макетной отличается. Меряем готовую строку и повторяем
   * замер, когда доедут шрифты, — до этого браузер считает по запасной
   * гарнитуре и капсула вышла бы не по имени.
   *
   * `useLayoutEffect`, а не `useEffect`: замер и перерисовка успевают до
   * отрисовки кадра, иначе плашка мигнула бы нулевой шириной.
   */
  const label = useRef<SVGTextElement>(null)
  const [plate, setPlate] = useState(0)
  useLayoutEffect(() => {
    const node = label.current
    if (!node) return
    let live = true
    // Трекинг добавляется и после последней буквы — эту добавку не считаем.
    const fit = (): void => {
      if (live) setPlate(node.getComputedTextLength() - PAWN_NAME.track + 2 * PAWN_PLATE.pad)
    }
    fit()
    void document.fonts?.ready.then(fit)
    return () => {
      live = false
    }
  }, [name])

  // Имя аватарки и номер набора вместе дают имя символа. Незнакомое имя
  // откатываем на первое: пустая фишка читалась бы поломкой, а не выбором.
  const face = PLAYER_AVATARS.includes(avatar) ? avatar : DEFAULT_AVATAR
  const box: CSSProperties = { left: x, top: y, width: size }
  const style = {
    ...box,
    color: markerColor(color),
  } as CSSProperties
  const viewBox = `${PAWN_FRAME.x} ${PAWN_FRAME.y} ${PAWN_FRAME.w} ${PAWN_FRAME.h}`

  return (
    <div className="player" style={style} role="img" aria-label={`${avatar} player marker`}>
      {/* Тень — отдельный слой: свой бокс, тот же кадр, что и у тела. */}
      <svg className="player__shadow" viewBox={viewBox} aria-hidden="true">
        <defs>
          {/*
            Градиент в долях бокса, а не в единицах кадра: он сам растягивается
            по эллипсу, и растушёвка не зависит от того, каким тот стал.
          */}
          <SceneShadow id={g('shadow')} />
        </defs>
        <ellipse
          cx={PAWN_SHADOW.cx}
          cy={PAWN_SHADOW.cy}
          rx={PAWN_SHADOW.rx}
          ry={PAWN_SHADOW.ry}
          fill={`url(#${g('shadow')})`}
        />
      </svg>

      <svg className="player__body" viewBox={viewBox}>
        <defs>
          {/*
            Окно: просто круг. Именно clipPath, а не mask: он обрезает и
            попадание курсора тоже, иначе невидимый квадрат аватарки
            перехватывал бы клики по булавке.
          */}
          <clipPath id={g('window')}>
            <circle cx={PAWN_WELL.cx} cy={PAWN_WELL.cy} r={PAWN_WELL.r} />
          </clipPath>
        </defs>

        <g transform={`translate(${PIN.x} ${PIN.y})`}>
          <path d={PIN_BODY} fill="currentColor" />
        </g>

        {/*
          Окно: подложка под головой, и это всё — ни кольца, ни обводки у него
          нет.

          Подложка полупрозрачная, той же плотности, что кружок в выборе
          аватарки, — `deep` в 60%. Она не красит окно сама, а затемняет то,
          что под ней: на капле это цвет игрока, поэтому сменить его целиком
          можно одним цветом маркера, и окно поедет за ним. Сплошной цвет так
          не умеет: окно становилось чёрным и от цвета капли не зависело.
        */}
        <circle
          cx={PAWN_WELL.cx}
          cy={PAWN_WELL.cy}
          r={PAWN_WELL.r}
          fill="var(--color-deep)"
          fillOpacity={0.6}
        />

        <g clipPath={`url(#${g('window')})`}>
          <use
            href={`#avatar-0-${face}`}
            x={AVATAR.x}
            y={AVATAR.y}
            width={AVATAR.size}
            height={AVATAR.size}
          />
        </g>

        {/*
          Ободок по кромке окна: чистый белый в полную силу. Полупрозрачный не
          годится — линия идёт по границе, половина её ложится на тёмное окно,
          половина на цветную каплю, и при малой силе видна только та половина,
          что на тёмном.

          Ширина — один пиксель экрана при любом размере фишки, `stroke-width`
          в единицах кадра дал бы на фишке поля десятую пикселя, а на превью
          жирную линию. Для этого `non-scaling-stroke`: он выключает пересчёт
          толщины вместе с системой координат.
        */}
        <circle
          cx={PAWN_WELL.cx}
          cy={PAWN_WELL.cy}
          r={PAWN_WELL.r}
          fill="none"
          stroke="#ffffff"
          strokeWidth={1}
          vectorEffect="non-scaling-stroke"
        />

        {name && (
          <>
            <rect
              x={(PAWN_FRAME.w - plate) / 2}
              y={PAWN_PLATE.bottom - PAWN_PLATE.h}
              width={plate}
              height={PAWN_PLATE.h}
              rx={PAWN_PLATE.h / 2}
              fill="currentColor"
            />
            {/*
            Трекинг в SVG добавляется и после последней буквы, поэтому
            середину строки сдвигаем на его половину — иначе подпись уедет
            влево. По вертикали строка стоит на середине плашки: базовая
            линия опущена на половину высоты прописной.
          */}
            <text
              ref={label}
              className="player__name"
              x={PAWN_FRAME.w / 2 + PAWN_NAME.track / 2}
              y={PAWN_PLATE.bottom - PAWN_PLATE.h / 2 + (PAWN_NAME.size * NAME_CAP) / 2}
              textAnchor="middle"
              fontSize={PAWN_NAME.size}
              letterSpacing={PAWN_NAME.track}
            >
              {name}
            </text>
          </>
        )}
      </svg>
    </div>
  )
}

/**
 * Свет на сцене один, и тень у всего на ней падает одинаково: плотное ядро,
 * быстрый спад и мягкий край (кадр `player` из Figma, узел 95:940). Это единственное
 * место в маркере, где заливка не плоская, и плоскому стилю оно не противоречит:
 * запрет размытых теней — про тень *под фигурой*, а здесь тень и есть фигура, у
 * которой нет кромки. Кубик и фишка берут одни и те же стопы.
 */
function SceneShadow({ id }: { id: string }) {
  return (
    <radialGradient id={id}>
      <stop stopColor="#000" stopOpacity="0.75" />
      <stop offset="0.45" stopColor="#000" stopOpacity="0.4" />
      <stop offset="0.75" stopColor="#000" stopOpacity="0.12" />
      <stop offset="1" stopColor="#000" stopOpacity="0" />
    </radialGradient>
  )
}
